import { useId } from "react";
import { Card } from "@/types/v2";
import { firestoreToDateTime } from "@/lib/utils";
import { container } from "@/repositories/container";
import { useCardMetricsOptions } from "@/hooks/useCardMetricsOptions";
import { Switch } from "@/components/Base/Switch";
import { RO_SECTION_TITLE_CLASS } from "@/components/Common/RoDetailsPanel/roPanelStyles";
import {
  customFormatDuration,
  getContainerDuration,
  getTotalTimeInShop,
  getAggregatedContainerDurations,
} from "@/lib/cardMetrics";

export interface RoMetricsSectionProps {
  cardData: Card;
}

const cardClass =
  "flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm";

export const RoMetricsSection = ({ cardData }: RoMetricsSectionProps) => {
  const {
    timestampsEnabled,
    setTimestampsEnabled,
    setAggregateTimesEnabled,
    aggregateTimesEnabled,
  } = useCardMetricsOptions();

  const timestampsId = useId();
  const combineId = useId();

  const hasHistory = (cardData.history?.length ?? 0) > 0;

  const rows = aggregateTimesEnabled
    ? getAggregatedContainerDurations(cardData.history).map((entry) => ({
        name: container.getContainerName(entry.containerId),
        enteredAt: null as string | null,
        duration: customFormatDuration(entry.totalDuration),
      }))
    : (cardData.history ?? []).map((entry) => ({
        name: container.getContainerName(entry.currentContainerId),
        enteredAt: timestampsEnabled
          ? (firestoreToDateTime(entry.enteredAt)?.toLocaleString() ?? null)
          : null,
        duration: customFormatDuration(getContainerDuration(entry)),
      }));

  return (
    <section aria-label="Metrics" className="flex flex-col gap-4">
      {/* Controls */}
      <div className={cardClass}>
        <h3 className={RO_SECTION_TITLE_CLASS}>RO Report</h3>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {!aggregateTimesEnabled && (
            <div className="flex items-center gap-2">
              <label
                htmlFor={timestampsId}
                className="cursor-pointer text-[13px] font-medium leading-none text-slate-600"
              >
                Show Timestamps
              </label>
              <Switch
                id={timestampsId}
                checked={timestampsEnabled}
                onCheckedChange={setTimestampsEnabled}
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <label
              htmlFor={combineId}
              className="cursor-pointer text-[13px] font-medium leading-none text-slate-600"
            >
              Combine Containers
            </label>
            <Switch
              id={combineId}
              checked={aggregateTimesEnabled}
              onCheckedChange={setAggregateTimesEnabled}
            />
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className={cardClass}>
        {hasHistory ? (
          <>
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase leading-none tracking-[0.04em] text-slate-400">
                Container
              </span>
              <span className="text-[11px] font-semibold uppercase leading-none tracking-[0.04em] text-slate-400">
                Duration
              </span>
            </div>
            {rows.map((row, index) => (
              <div
                key={index}
                className="flex items-start justify-between gap-2"
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-[13px] leading-none text-slate-700">
                    {row.name}
                  </span>
                  {row.enteredAt && (
                    <span className="text-[11px] leading-none text-slate-400">
                      {row.enteredAt}
                    </span>
                  )}
                </div>
                <span className="shrink-0 text-right text-[13px] leading-none text-slate-500">
                  {row.duration}
                </span>
              </div>
            ))}
            <div className="h-px bg-slate-200" />
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-semibold leading-none text-slate-900">
                Total Container Time
              </span>
              <span className="text-[13px] font-semibold leading-none text-slate-900">
                {getTotalTimeInShop(cardData.history)}
              </span>
            </div>
          </>
        ) : (
          <div className="text-center text-[13px] text-slate-500">
            No container history available
          </div>
        )}
      </div>
    </section>
  );
};
